const APPLICATION_STATUS = {
    PENDING: 1,
    APPROVED: 2,
    REJECTED: 3,
    WITHDRAWN: 4,
};

const CURRENT_CERTIFICATION_STATUS = {
    ACTIVE: 1,
    EXPIRED: 2,
    REVOKED: 3,
    SUSPENDED: 4,
};

const asText = (value) => typeof value === "string" ? value.trim() : "";

const displayTime = (value) => value == null ? "-" : value;

const applicationStatusToViewStatus = (status) => {
    if (status === null || status === undefined || status === "") return "UNKNOWN";
    const statusCode = Number(status);
    if (statusCode === APPLICATION_STATUS.PENDING) return "PENDING";
    if (statusCode === APPLICATION_STATUS.APPROVED) return "APPROVED";
    if (statusCode === APPLICATION_STATUS.REJECTED) return "REJECTED";
    if (statusCode === APPLICATION_STATUS.WITHDRAWN) return "WITHDRAWN";
    return "UNKNOWN";
};

const currentStatusToViewStatus = (status) => {
    if (status === null || status === undefined || status === "") return "UNVERIFIED";
    const statusCode = Number(status);
    if (statusCode === CURRENT_CERTIFICATION_STATUS.ACTIVE) return "APPROVED";
    if (statusCode === CURRENT_CERTIFICATION_STATUS.EXPIRED) return "EXPIRED";
    if (statusCode === CURRENT_CERTIFICATION_STATUS.REVOKED) return "REVOKED";
    if (statusCode === CURRENT_CERTIFICATION_STATUS.SUSPENDED) return "SUSPENDED";
    return "UNKNOWN";
};

const isSupportedImageSignature = (bytes) => {
    if (!bytes || bytes.length < 3) return false;

    const isJpeg = bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
    const isPng = bytes.length >= 8 &&
        bytes[0] === 0x89 &&
        bytes[1] === 0x50 &&
        bytes[2] === 0x4e &&
        bytes[3] === 0x47 &&
        bytes[4] === 0x0d &&
        bytes[5] === 0x0a &&
        bytes[6] === 0x1a &&
        bytes[7] === 0x0a;

    return isJpeg || isPng;
};

const normalizeApplication = (application = {}) => ({
    id: application.id,
    status: applicationStatusToViewStatus(application.applicationStatus),
    schoolName: asText(application.schoolName),
    schoolLocation: [asText(application.provinceName), asText(application.cityName)].filter(Boolean).join(" "),
    enrollmentMonth: displayTime(application.enrollmentMonth),
    expectedGraduationMonth: displayTime(application.expectedGraduationMonth),
    appliedAt: displayTime(application.submittedAt),
    reviewedAt: displayTime(application.finishedAt),
    reason: asText(application.visibleReason),
});

const createVerification = (status, values = {}) => {
    const verification = {
        status,
        schoolName: asText(values.schoolName),
        schoolLocation: asText(values.schoolLocation),
        enrollmentMonth: displayTime(values.enrollmentMonth),
        expectedGraduationMonth: displayTime(values.expectedGraduationMonth),
        validUntil: displayTime(values.validUntil),
        reviewedAt: displayTime(values.reviewedAt),
        reason: asText(values.reason),
        pendingApplicationId: values.pendingApplicationId || null,
    };

    if (values.hasPendingApplication) {
        verification.hasPendingApplication = true;
    }

    return verification;
};

const normalizeCampusOverview = (data = {}) => {
    const applications = Array.isArray(data.applications)
        ? data.applications.map(normalizeApplication)
        : [];
    const current = data.currentCertification || null;
    const pendingApplication = applications.find(({ status }) => status === "PENDING");
    const latestApplication = applications[0];
    const status = currentStatusToViewStatus(current && current.certificationStatus);
    const currentValues = current ? {
        schoolName: current.schoolName,
        schoolLocation: [asText(current.provinceName), asText(current.cityName)].filter(Boolean).join(" "),
        enrollmentMonth: current.enrollmentMonth,
        expectedGraduationMonth: current.expectedGraduationMonth,
        validUntil: current.validUntil,
        reviewedAt: current.certifiedAt,
        reason: current.visibleReason,
    } : {};
    let verification;

    if (status !== "UNVERIFIED") {
        verification = createVerification(status, {
            ...currentValues,
            pendingApplicationId: pendingApplication && pendingApplication.id,
            hasPendingApplication: !!pendingApplication,
        });
    } else if (pendingApplication) {
        verification = createVerification("PENDING", {
            pendingApplicationId: pendingApplication.id,
        });
    } else if (latestApplication && latestApplication.status === "UNKNOWN") {
        verification = createVerification("UNKNOWN");
    } else if (latestApplication && latestApplication.status === "REJECTED") {
        verification = createVerification("REJECTED", {
            reason: latestApplication.reason,
        });
    } else if (latestApplication && latestApplication.status === "WITHDRAWN") {
        verification = createVerification("WITHDRAWN");
    } else {
        verification = createVerification("UNVERIFIED");
    }

    return {
        verification,
        history: applications,
        campusCertificationReminderDismissed: data.campusCertificationReminderDismissed === true,
    };
};

const getCreatorCampusPresentation = (overview = {}) => {
    const verification = overview.verification || {};

    if (verification.status === "APPROVED") {
        return { showCampusBadge: true, reminder: null };
    }

    if (verification.status === "UNKNOWN") {
        return { showCampusBadge: false, reminder: null };
    }

    if (verification.status === "PENDING" || verification.hasPendingApplication) {
        return {
            showCampusBadge: false,
            reminder: {
                message: "校园认证审核中，审核通过后即可接单校园类型主题",
                dismissible: false,
            },
        };
    }

    if (overview.campusCertificationReminderDismissed === true) {
        return { showCampusBadge: false, reminder: null };
    }

    return {
        showCampusBadge: false,
        reminder: {
            message: "完成校园认证后，即可接单校园类型主题",
            dismissible: true,
        },
    };
};

module.exports = {
    APPLICATION_STATUS,
    CURRENT_CERTIFICATION_STATUS,
    applicationStatusToViewStatus,
    currentStatusToViewStatus,
    isSupportedImageSignature,
    displayTime,
    normalizeCampusOverview,
    getCreatorCampusPresentation,
};
